import * as _ from 'lodash';

import { imageExtensions, videoExtensions } from './file-type';
import { imagesEndpoint, orderImagesEndpoint, referralImagesEndpoint } from 'shared/models/api.model';
import { restEndpoint } from 'config';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const docIcon = '/assets/img/file-icons/doc.svg';
const videoIcon = '/assets/img/file-icons/video.svg';
const imageIcon = '/assets/img/file-icons/image.svg';
const pdfIcon = '/assets/img/file-icons/pdf.svg';
const zipIcon = '/assets/img/file-icons/zip.svg';
const docxIcon = '/assets/img/file-icons/docx.svg';
const xlsIcon = '/assets/img/file-icons/xls.svg';
const rtfIcon = '/assets/img/file-icons/rtf.svg';
const aiIcon = '/assets/img/file-icons/ai.svg';

const icons = {
    ..._.zipObject(
        imageExtensions,
        _.map(imageExtensions, () => imageIcon)
    ),
    ..._.zipObject(
        videoExtensions,
        _.map(videoExtensions, () => videoIcon)
    ),
    // Non media files
    csv: xlsIcon,
    xls: xlsIcon,
    xlsx: xlsIcon,
    pdf: pdfIcon,
    rtf: rtfIcon,
    ai: aiIcon,
    zip: zipIcon,
    doc: docxIcon,
    docx: docxIcon,
    odt: docxIcon
};

export function getIconLink(extension) {
    return icons[extension] || docIcon;
}

export function getOrientation(width, height) {
    if (width < height) {
        return 'ADD_BRAND_IMAGE_FILE_DATA_ORIENTATION_PORTRAIT';
    } else if (width > height) {
        return 'ADD_BRAND_IMAGE_FILE_DATA_ORIENTATION_LANDSCAPE';
    } else {
        return 'ADD_BRAND_IMAGE_FILE_DATA_ORIENTATION_SQUARE';
    }
}

export function getImageUrl(imageId: string, size: string, extension?: string): string {
    const imageUrlWithId = `${imagesEndpoint}/${imageId}`;
    if (!extension) {
        return `${restEndpoint}/internal${imageUrlWithId}?size=${size}`;
    }

    return `${restEndpoint}/internal${imageUrlWithId}?size=${size}&extension=${extension}`;
}

export function getOrderAreaImageUrl(imageId: string, size: string): string {
    const imageUrlWithId = `${orderImagesEndpoint}/${imageId}`;
    return `${restEndpoint}/internal${imageUrlWithId}?size=${size}`;
}

export function getReferralBrandLogoUrl(referralId: string): string {
    const imageUrlWithId = `${referralImagesEndpoint}/${referralId}`;
    return `${restEndpoint}/internal${imageUrlWithId}`;
}

export function getFolderImageUrl(imageId: string, extension: string): string {
    const imageUrlWithId = `${imagesEndpoint}/${imageId}`;
    return `${restEndpoint}/internal${imageUrlWithId}?format=${extension}`;
}

export const productImagePlaceholder = 'assets/img/placeholder.png';

export const handleImageUpload = (
    file: File,
    fileConstraints: { minSize: number; maxSize: number; fileExtensions: string[] }
) =>
    mapFileToObservable(file).pipe(
        map((fileResult) => {
            const fileExtensionPosition = file.name.lastIndexOf('.');

            if (fileExtensionPosition <= 0) {
                return { errorKey: ImageUploadError.NotReadable };
            }
            const extension = file.name.substring(fileExtensionPosition + 1).toLowerCase();
            if (!_.includes(fileConstraints.fileExtensions, extension)) {
                return { errorKey: ImageUploadError.InvalidFileExtension };
            }
            if (file.size < fileConstraints.minSize) {
                return { errorKey: ImageUploadError.FileSizeTooSmall };
            }
            if (file.size > fileConstraints.maxSize) {
                return { errorKey: ImageUploadError.FileSizeTooLarge };
            }

            const fileData = {
                name: file.name,
                extension: file.name.substring(fileExtensionPosition + 1),
                size: file.size,
                content: fileResult.result
            };

            return fileData;
        })
    );

export const mapFileToObservable = (file: File, reader: FileReader = new FileReader()) =>
    new Observable<{ result: FileReader['result'] }>((subscriber) => {
        if (!(file instanceof File)) {
            subscriber.error(new Error('`file` must be an instance of File.'));
            return;
        }

        reader.onerror = (err) => subscriber.error(err);
        reader.onabort = (err) => subscriber.error(err);
        reader.onload = (event) => {
            if (event.target.readyState === reader.DONE) {
                return subscriber.next({ result: reader.result });
            }
        };
        reader.onloadend = () => subscriber.complete();

        reader.readAsDataURL(file);
    });

export enum ImageResolution {
    Thumb = 'thumb',
    Large = 'large',
    Medium = 'medium',
    Original = 'original'
}

export interface Image {
    _id: string;
    hash: string;
    isMainImage: boolean;
    productView: string;
    isPreliminary?: boolean;
    isAIImage?: boolean;
}

export enum ImageUploadError {
    FileSizeTooSmall = 'MIN_FILESIZE_NOT_REACHED',
    FileSizeTooLarge = 'MAX_FILESIZE_EXCEEDED',
    InvalidFileExtension = 'INVALID_FILE_EXTENSION',
    NotReadable = 'NOT_READABLE'
}
